import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store/store';
import { usersService } from '../services/usersService';
import { organizationsService } from '../services/organizations.service';
import { toast } from 'react-toastify';
import { UserRole, Organization } from '../types';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';

interface UserFormData {
  email: string;
  password?: string;
  fullName: string;
  role: UserRole;
  isActive?: boolean;
  organizationId?: string;
  transferPermissions?: string[];
}

const UserFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isPlatformAdmin = currentUser?.role === UserRole.PLATFORM_ADMIN;

  const [loading, setLoading] = useState(isEditMode);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedTransferPermissions, setSelectedTransferPermissions] = useState<string[]>([]);
  const [userName, setUserName] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dependencies, setDependencies] = useState<{ canDelete: boolean; reasons: string[] } | undefined>();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UserFormData>({
    defaultValues: {
      isActive: true,
      role: UserRole.STAFF,
    },
  });

  const selectedRole = watch('role');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load organizations if PLATFORM_ADMIN (for dropdown)
        if (isPlatformAdmin && !isEditMode) {
          const orgsData = await organizationsService.getAll();
          setOrganizations(orgsData);
        }

        // Load user data if editing
        if (id) {
          const user = await usersService.getById(id);
          setUserName(user.fullName);
          reset({
            email: user.email,
            fullName: user.fullName,
            role: user.role,
            isActive: user.isActive,
          });
          // Set transfer permissions if they exist
          if (user.transferPermissions && user.transferPermissions.length > 0) {
            setSelectedTransferPermissions(user.transferPermissions);
          }
        }
      } catch (error: any) {
        console.error('Failed to load data:', error);
        toast.error(t('forms.failedToLoad'));
        if (id) navigate('/app/users');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isPlatformAdmin, isEditMode, navigate, reset, currentUser?.role]);

  const onSubmit = async (data: UserFormData) => {
    try {
      console.log('Submitting user data:', data);

      // Clean up data
      const cleanedData: any = {
        email: data.email,
        fullName: data.fullName,
        role: data.role,
        isActive: data.isActive,
      };

      // Only include password if it's provided
      if (data.password && data.password.trim()) {
        cleanedData.password = data.password;
      }

      // Include organizationId if PLATFORM_ADMIN is creating a user
      if (isPlatformAdmin && !isEditMode && data.organizationId) {
        cleanedData.organizationId = data.organizationId;
      }

      // Include transfer permissions for STAFF
      if (data.role === UserRole.STAFF && selectedTransferPermissions.length > 0) {
        cleanedData.transferPermissions = selectedTransferPermissions;
      } else if (data.role === UserRole.STAFF) {
        // Empty array means "no permissions" (view only)
        cleanedData.transferPermissions = [];
      }

      if (isEditMode && id) {
        await usersService.update(id, cleanedData);
        toast.success(t('forms.userUpdated'));
      } else {
        // Password is required for new users
        if (!data.password || !data.password.trim()) {
          toast.error(t('forms.passwordRequiredNew'));
          return;
        }
        await usersService.create(cleanedData);
        toast.success(t('forms.userCreated'));
      }

      navigate('/app/users');
    } catch (error: any) {
      console.error('Failed to save user:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        t('forms.failedToSave');
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = async () => {
    if (!id) return;

    try {
      const deps = await usersService.checkDependencies(id);
      setDependencies(deps);
    } catch (error) {
      setDependencies(undefined);
    }
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;

    await usersService.delete(id);
    toast.success(t('users.deleted'));
    setIsDeleteModalOpen(false);
    navigate('/app/users');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {isEditMode ? t('forms.editUser') : t('forms.newUser')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          {isEditMode
            ? t('forms.updateUserInfo')
            : t('forms.addNewUser')}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        <div className="bg-white shadow sm:rounded-lg p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                {...register('fullName', { required: 'Full name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('forms.fullNamePlaceholder')}
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="user@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password {isEditMode ? '(leave empty to keep current)' : '*'}
              </label>
              <input
                type="password"
                {...register('password', {
                  required: !isEditMode ? 'Password is required' : false,
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('forms.passwordPlaceholder')}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Minimum 6 characters
              </p>
            </div>

            {/* Organization (only for PLATFORM_ADMIN creating new users) */}
            {isPlatformAdmin && !isEditMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization *
                </label>
                <select
                  {...register('organizationId', { required: 'Organization is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                {errors.organizationId && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.organizationId.message}
                  </p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Select which organization this user belongs to
                </p>
              </div>
            )}

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                {...register('role', { required: 'Role is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={UserRole.STAFF}>Staff</option>
                <option value={UserRole.MANAGER}>Manager</option>
                <option value={UserRole.ORG_ADMIN}>Organization Admin</option>
                {isPlatformAdmin && (
                  <option value={UserRole.PLATFORM_ADMIN}>Platform Admin</option>
                )}
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.role.message}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                <strong>Staff:</strong> Can view all transfers with optional add/edit/delete permissions<br />
                <strong>Manager:</strong> Can create and edit transfers, view pricing<br />
                <strong>Org Admin:</strong> Full organization management
              </p>
            </div>

            {/* Transfer Permissions (for STAFF role) */}
            {selectedRole === UserRole.STAFF && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transfer Permissions
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="permission-add"
                      checked={selectedTransferPermissions.includes('add')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTransferPermissions([...selectedTransferPermissions, 'add']);
                        } else {
                          setSelectedTransferPermissions(selectedTransferPermissions.filter(p => p !== 'add'));
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="permission-add" className="ml-2 block text-sm text-gray-900">
                      Add - Can create new transfers
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="permission-edit"
                      checked={selectedTransferPermissions.includes('edit')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTransferPermissions([...selectedTransferPermissions, 'edit']);
                        } else {
                          setSelectedTransferPermissions(selectedTransferPermissions.filter(p => p !== 'edit'));
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="permission-edit" className="ml-2 block text-sm text-gray-900">
                      Edit - Can update existing transfers
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="permission-delete"
                      checked={selectedTransferPermissions.includes('delete')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTransferPermissions([...selectedTransferPermissions, 'delete']);
                        } else {
                          setSelectedTransferPermissions(selectedTransferPermissions.filter(p => p !== 'delete'));
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="permission-delete" className="ml-2 block text-sm text-gray-900">
                      Delete - Can remove transfers
                    </label>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Leave unchecked for view-only access. Select specific permissions to grant.
                </p>
              </div>
            )}

            {/* Active Status (only show in edit mode) */}
            {isEditMode && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
          {isEditMode ? (
            <button
              type="button"
              onClick={handleDeleteClick}
              className="px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors min-h-[44px] font-medium"
            >
              {t('common.delete')}
            </button>
          ) : (
            <div />
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => navigate('/app/users')}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 transition-colors min-h-[44px] font-medium"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:bg-blue-400 min-h-[44px] font-medium"
            >
              {isSubmitting
                ? t('common.saving')
                : isEditMode
                ? t('common.update')
                : t('common.create')}
            </button>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDependencies(undefined);
        }}
        onConfirm={handleDeleteConfirm}
        entityName={userName}
        entityType="user"
        requirePassword={true}
        dependencies={dependencies}
      />
    </div>
  );
};

export default UserFormPage;
